// Content for the first-run onboarding walkthrough shown on top of the app after signup.
// Each step drives one popup card in OnboardingTour. The copy is drawn from the Help Center
// guide (src/components/guide/guideData.js) so the two stay in the same voice.
//
// Fields:
//   id        stable key for React
//   selector  optional CSS selector for the real element to spotlight. When present and the
//             element is on screen, the card anchors to it and everything else dims. When it
//             is missing (a step about a state that only exists mid-search), the card centers.
//   tag       small eyebrow label above the title
//   title     the headline
//   body      one short paragraph, plain and benefit led
//   points    optional short list rendered as bullets

export const TOUR_STEPS = [
  {
    id: 'welcome',
    tag: 'Welcome',
    title: 'Welcome to RepBuddy',
    body:
      'RepBuddy finds real, verified decision makers from a single sentence. No filters to wrestle, no dead inboxes. This quick tour takes about a minute. You can leave any time with the X in the corner and pick it back up later from your account menu.',
  },
  {
    id: 'seller-profile',
    selector: '[data-tour="seller-profile"]',
    tag: 'Foundation',
    title: 'Start with your Seller Profile',
    body:
      'This is the one thing to do before your first hunt. Tell RepBuddy who you sell to and what you sell. Every search reads from this profile, so anything you leave out of a request is filled in from here. Set it once and it saves to the cloud, following you across devices.',
  },
  {
    id: 'ask',
    selector: '[data-tour="chat-input"]',
    tag: 'Find people',
    title: 'Just ask, in plain English',
    body:
      'Type what you want the way you would say it, for example "Find 10 VPs of Sales at Cisco." RepBuddy reads the request, fills in the rest from your Seller Profile, and gets to work. State the count you want for a fuller list.',
  },
  {
    id: 'estimate',
    tag: 'Credits',
    title: 'Always know the cost first',
    body:
      'Before any paid task runs, RepBuddy shows the most lead credits it could use and how many you have left. Nothing is spent until you confirm.',
    points: [
      'You spend a lead credit only on a net-new contact that passes verification.',
      'Research, drafting, and chat are always free.',
    ],
  },
  {
    id: 'results',
    tag: 'Find people',
    title: 'Every contact is checked before you see it',
    body:
      'Each email is proven deliverable against the live mail server, and each person is confirmed to still hold the role. Contacts that fail either check never reach your list, and you are never charged for them. When a run finishes, RepBuddy shows exactly how many credits were used.',
  },
  {
    id: 'deep-hunt',
    selector: '[data-tour="chat-input"]',
    tag: 'Find people',
    title: 'Target companies, not just titles',
    body:
      'When the account matters more than the exact title, ask for companies like your best customers and the roles you want at each. RepBuddy finds matching companies, vets every one, then pulls the right people inside them. Try "Find 2 people at each company like ours in California."',
  },
  {
    id: 'attach',
    selector: '[data-tour="attach"]',
    tag: 'Your lists',
    title: 'Already have a list? Attach it',
    body:
      'Drop in a CSV and RepBuddy reads the columns to offer the right action. A list of emails gives you Verify All Emails. A list of company names gives you Find Contacts, Find Websites, and Enrich Companies. Your original columns are always kept and new data is added alongside them.',
  },
  {
    id: 'research',
    tag: 'Free',
    title: 'Research anyone, no credits',
    body:
      'Ask "What does this company do?" or open a contact and ask about the person. You get a clear Company or Person Brief built from live context, and it never touches your credits.',
  },
  {
    id: 'drafting',
    tag: 'Enrich and write',
    title: 'Turn any contact into a first email',
    body:
      'Ask RepBuddy to draft an opener for a contact and it writes one shaped by the tone, call to action, and phrasing in your Seller Profile. Then revise it conversationally: "make it shorter," "mention their recent funding," "lead with the problem." Drafting is always free, so iterate as much as you like.',
  },
  {
    id: 'edit',
    tag: 'Manage your work',
    title: 'Change your mind any time',
    body:
      'If a result is not quite right, edit your original message and RepBuddy regenerates a fresh reply. Your earlier version is kept too, so you never lose work by trying another angle.',
  },
  {
    id: 'history',
    selector: '[data-tour="chat-list"]',
    tag: 'Manage your work',
    title: 'Your history is always here',
    body:
      'Each hunt becomes a chat session in the sidebar, saved to the cloud and waiting after you log back in or move to another device. Use New search for a different task, reopen any past session to see its results, and export any list to CSV for your CRM whenever you want.',
  },
  {
    id: 'account',
    selector: '[data-tour="account"]',
    tag: 'Your account',
    title: 'Track your plan and usage',
    body:
      'Open the account menu any time to review your plan, your remaining lead credits and allowances, and your billing. You are all set. Run your first hunt and see verified contacts in under a minute.',
  },
];
